"use client";

import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

interface PublicationItem {
  id: string | number;
  citation: string;
  link?: string;
  indexed?: string;
  year?: string | number;
}

interface PublicationsSectionsProps {
  journalPapers: PublicationItem[];
  bookChapters: PublicationItem[];
  conferencePapers: PublicationItem[];
}

function ExternalLinkIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

/** Generic expand/collapse wrapper, animated with a grid-rows trick (no JS height math needed). */
function Collapsible({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

function SectionHeader({
  title,
  count,
  open,
  onClick,
}: {
  title: string;
  count: number;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between gap-4 py-5 px-1 text-left group"
    >
      <span className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-primary transition-colors">
        {title}
        <span className="ml-3 align-middle text-sm font-medium text-gray-500">({count})</span>
      </span>
      <ChevronDown
        className={`w-6 h-6 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

function YearHeader({
  year,
  count,
  open,
  onClick,
}: {
  year: string;
  count: number;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between gap-4 py-3 px-4 text-left bg-muted/50 hover:bg-muted rounded-lg transition-colors"
    >
      <span className="text-lg font-semibold text-gray-800">
        {year}
        <span className="ml-2 text-sm font-medium text-gray-500">({count})</span>
      </span>
      <ChevronDown
        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

function PaperCard({
  item,
  linkLabel,
  badgeClassName,
}: {
  item: PublicationItem;
  linkLabel: string;
  badgeClassName?: string;
}) {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <p className="text-gray-800 leading-relaxed text-sm lg:text-base">{item.citation}</p>
            {item.indexed && (
              <span
                className={`inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full ${
                  badgeClassName ?? "bg-blue-100 text-blue-800"
                }`}
              >
                {item.indexed}
              </span>
            )}
          </div>
          {item.link && (
            <div className="flex-shrink-0">
              <Button asChild variant="secondary" size="sm">
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <span>{linkLabel}</span>
                  <ExternalLinkIcon />
                </a>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PublicationsSections({
  journalPapers,
  bookChapters,
  conferencePapers,
}: PublicationsSectionsProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    journal: true,
  });
  const [openYears, setOpenYears] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleYear = (year: string) =>
    setOpenYears((prev) => ({ ...prev, [year]: !prev[year] }));

  // Group journal papers by year, newest first. Anything without a year goes last, under "Other".
  const journalByYear = useMemo(() => {
    const groups = new Map<string, PublicationItem[]>();
    journalPapers.forEach((p) => {
      const key = p.year ? String(p.year) : "Other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return Number(b) - Number(a);
    });
  }, [journalPapers]);

  return (
    <div className="space-y-4">
      {/* Journal Papers */}
      <section className="border-b border-gray-200">
        <SectionHeader
          title="Journal Papers (SCI/SCIE/Web of Science/Scopus Indexed)"
          count={journalPapers.length}
          open={!!openSections.journal}
          onClick={() => toggleSection("journal")}
        />
        <Collapsible open={!!openSections.journal}>
          <div className="pb-8 space-y-3">
            {journalByYear.map(([year, papers]) => (
              <div key={year} className="space-y-3">
                <YearHeader
                  year={year}
                  count={papers.length}
                  open={!!openYears[year]}
                  onClick={() => toggleYear(year)}
                />
                <Collapsible open={!!openYears[year]}>
                  <div className="space-y-4 pt-1 pb-2">
                    {papers.map((paper) => (
                      <PaperCard key={paper.id} item={paper} linkLabel="View Paper" />
                    ))}
                  </div>
                </Collapsible>
              </div>
            ))}
          </div>
        </Collapsible>
      </section>

      {/* Book Chapters */}
      <section className="border-b border-gray-200">
        <SectionHeader
          title="Book Chapters Published"
          count={bookChapters.length}
          open={!!openSections.books}
          onClick={() => toggleSection("books")}
        />
        <Collapsible open={!!openSections.books}>
          <div className="pb-8 space-y-4">
            {bookChapters.map((chapter) => (
              <PaperCard key={chapter.id} item={chapter} linkLabel="View Chapter" />
            ))}
          </div>
        </Collapsible>
      </section>

      {/* Conference Papers */}
      <section>
        <SectionHeader
          title="Refereed Conference Research Papers (CORE Ranked/Scopus Indexed)"
          count={conferencePapers.length}
          open={!!openSections.conference}
          onClick={() => toggleSection("conference")}
        />
        <Collapsible open={!!openSections.conference}>
          <div className="pb-8 space-y-4">
            {conferencePapers.map((paper) => (
              <PaperCard
                key={paper.id}
                item={paper}
                linkLabel="View Paper"
                badgeClassName={
                  paper.indexed?.includes("A*")
                    ? "bg-green-100 text-green-800"
                    : "bg-orange-100 text-orange-800"
                }
              />
            ))}
          </div>
        </Collapsible>
      </section>
    </div>
  );
}
